/**
 * The window's one voice call (RFC 0307).
 *
 * What a call does with its session is `voiceCallSession` in core. This is
 * the window's part: one call at a time, the composer's and the pill's view of
 * it, and the screen the voice is told about.
 *
 * A call belongs to a session, not to the pane showing it: switching spaces or
 * closing the thread leaves it up. So the call lives here, for the life of the
 * window, and reads its session through repo handles rather than through a
 * mounted thread. A composer showing that session draws the live call; the
 * header pill draws it from anywhere. One call at a time: starting another
 * ends the first.
 */

import type { Repo } from '@automerge/automerge-repo/slim'
import {
  formatView,
  type MessageView,
  readHoldingsPrompt,
} from '@mod/core/model'
import { voiceCallSession } from '@mod/core/voice'
import type { WindowBillingDispatch } from '../../services/sharing/billingDispatch'
import {
  chosenVoiceProvider,
  voiceProviderId,
} from '../../services/voice/choice'
import { VoiceController } from '../../services/voice/controller'
import { fetchVoiceSecret } from '../../services/voice/secret'
import type { ComposerVoice } from '../../services/voice/types'
import { holdingRepo } from '../../services/world/repo/held-docs'
import { preferredComposerModel } from './Composer'
import { agentFor } from './startSession'

export interface CallTarget {
  repo: Repo
  sessionId: string
  projectId?: string
  title: string
}

export interface ActiveCall {
  sessionId: string
  projectId?: string
  title: string
  voice: ComposerVoice
}

const idleVoice = (start: () => void): ComposerVoice => ({
  status: 'idle',
  micMuted: false,
  speakerMuted: false,
  level: 0,
  start,
  stop: () => {},
  toggleMic: () => {},
  toggleSpeaker: () => {},
})

class SessionCalls {
  private billing: WindowBillingDispatch | undefined

  /** Installed by the enrolled agent host; clearing it never restores legacy
   * credentials while sharing is enabled. */
  setBillingDispatch = (dispatch: WindowBillingDispatch): (() => void) => {
    this.billing = dispatch
    return () => {
      if (this.billing === dispatch) this.billing = undefined
    }
  }

  private call: {
    target: CallTarget
    controller: VoiceController
    detach: () => void
    /** The live plan, once the controller has asked for it: how a put-through
     *  moves the call (RFC 0336 S8). */
    plan: ReturnType<typeof voiceCallSession> | null
    /** Lets go of the documents the call found (RFC 0317 3d). */
    release: () => void
  } | null = null
  private listeners = new Set<() => void>()
  private active: ActiveCall | null = null
  /** What the window shows, as the agent's `<view>` is built (RFC 0304 3d). */
  private viewOf: ((sessionId: string) => MessageView | null) | null = null
  /** How the window hands a call back to the World agent (RFC 0336 3d). */
  private toWorld: (() => boolean) | null = null
  /** The project a session is in, as the window knows it (RFC 0336 S1). */
  private projectOf: ((sessionId: string) => string | undefined) | null = null

  /** The window says how to find a session's project. Returns the undo. */
  setProjectOf = (
    read: (sessionId: string) => string | undefined,
  ): (() => void) => {
    this.projectOf = read
    return () => {
      if (this.projectOf === read) this.projectOf = null
    }
  }

  /**
   * The window says how "back to World" is done: put the call through to the
   * World agent and show its thread. Returns the undo, for the unmount.
   */
  setBackToWorld = (go: () => boolean): (() => void) => {
    this.toWorld = go
    return () => {
      if (this.toWorld === go) this.toWorld = null
    }
  }

  /**
   * The window says how to read its screen, so the voice can see what the
   * person sees. Returns the undo, for the window's unmount.
   */
  setViewReader = (
    read: (sessionId: string) => MessageView | null,
  ): (() => void) => {
    this.viewOf = read
    return () => {
      if (this.viewOf === read) this.viewOf = null
    }
  }

  /** The person's screen and what they pointed at, as the agent reads them. */
  screenFor = async (
    target: Pick<CallTarget, 'repo' | 'sessionId' | 'projectId'>,
  ): Promise<string> => {
    const view = formatView(this.viewOf?.(target.sessionId), target.sessionId)
    const held = await readHoldingsPrompt(
      target.repo,
      target.sessionId,
      target.projectId,
    ).catch(() => '')
    return [view, held].filter(Boolean).join('\n')
  }

  subscribe = (l: () => void): (() => void) => {
    this.listeners.add(l)
    return () => this.listeners.delete(l)
  }

  /** The call that is on, connecting, or failed and still saying why. */
  current = (): ActiveCall | null => this.active

  /** What a composer on `target` draws: the live call, or a button to start one. */
  voiceFor(target: CallTarget): ComposerVoice {
    if (this.active?.sessionId === target.sessionId) return this.active.voice
    return idleVoice(() => this.start(target))
  }

  start(target: CallTarget): void {
    if (this.call?.target.sessionId === target.sessionId) {
      this.call.controller.start()
      return
    }
    this.end()
    // The call keeps the handles it finds, so its finds hold until it ends.
    const holding = holdingRepo(target.repo)
    const controller = new VoiceController({
      provider: chosenVoiceProvider(),
      fetchSecret: () =>
        fetchVoiceSecret({
          provider: voiceProviderId(),
          sessionId: target.sessionId,
          billing: this.billing,
        }),
      // What the call is for lives in core (`voiceCallSession`); the window
      // adds only what it alone knows: its screen, and how a typed send to this
      // session is addressed.
      plan: () => {
        const plan = voiceCallSession({
          repo: holding.repo,
          sessionId: target.sessionId,
          ...(target.projectId ? { projectId: target.projectId } : {}),
          title: () => target.title,
          hangUp: () => controller.stop(),
          retitle,
          screen: () => this.screenFor(target),
          // Read when called, so a window that registers after the call
          // started still answers it.
          backToWorld: () => this.toWorld?.() ?? false,
          // An answer to a session in another project is recorded there.
          projectOf: (id) => this.projectOf?.(id),
          address: () => {
            const model = preferredComposerModel()
            const agent = agentFor(model)
            return {
              ...(model.provider ? { requestedModel: model } : {}),
              ...(agent ? { agent } : {}),
            }
          },
        })
        if (this.call?.controller === controller) this.call.plan = plan
        return plan
      },
    })
    // The session's name as the registry has it now: a call started from the
    // new pane begins on a stub title the agent renames after its first turn.
    const retitle = (title: string) => {
      if (!title || title === target.title) return
      target.title = title
      if (this.active?.sessionId !== target.sessionId) return
      this.active = { ...this.active, title }
      this.emit()
    }
    const detach = controller.subscribe(() => {
      const voice = controller.snapshot()
      // A call that has hung up is gone; one that failed stays so the
      // composer can say why, until the next start.
      this.active =
        voice.status === 'idle'
          ? null
          : {
              sessionId: target.sessionId,
              ...(target.projectId ? { projectId: target.projectId } : {}),
              title: target.title,
              voice,
            }
      this.emit()
    })
    this.call = {
      target,
      controller,
      detach,
      plan: null,
      release: holding.release,
    }
    controller.start()
  }

  /**
   * Put the call through to another session (RFC 0336 S8): the same call, now
   * on `next`. The voice says who it is talking to; nothing hangs up. With no
   * call on, nothing happens: putting someone through does not start one.
   */
  retarget(next: CallTarget): void {
    const call = this.call
    if (!call || call.target.sessionId === next.sessionId) return
    // The target object is read by the plan's closures (the title, the
    // screen), so it is updated in place rather than replaced.
    call.target.sessionId = next.sessionId
    call.target.title = next.title
    if (next.projectId) call.target.projectId = next.projectId
    else delete call.target.projectId
    call.plan?.retarget(
      {
        sessionId: next.sessionId,
        ...(next.projectId ? { projectId: next.projectId } : {}),
      },
      `You have just been put through to "${next.title}". From now on, what ` +
        'the person says goes to that session. Tell them in one short sentence ' +
        'that they are now talking to it.',
    )
    if (this.active) {
      this.active = {
        ...this.active,
        sessionId: next.sessionId,
        ...(next.projectId ? { projectId: next.projectId } : {}),
        title: next.title,
      }
      if (!next.projectId) delete this.active.projectId
      this.emit()
    }
  }

  /** Hang up whatever call is on. */
  end = (): void => {
    const call = this.call
    if (!call) return
    this.call = null
    call.controller.stop()
    call.detach()
    call.release()
    this.active = null
    this.emit()
  }

  private emit(): void {
    for (const l of this.listeners) l()
  }
}

/**
 * One per window, kept on `globalThis` for the reason `liveCalls` is: a hot
 * reload loads a fresh copy of this module, and a fresh registry would draw
 * the composer idle while the old copy's call is still talking. The live call
 * keeps the code it started with; the next call picks up the new code.
 */
const REGISTRY = '__modSessionCalls'
export const sessionCalls: SessionCalls = ((
  globalThis as unknown as Record<string, SessionCalls | undefined>
)[REGISTRY] ??= new SessionCalls())
